import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"

import { AccessPolicyDialog } from "./access-policy-dialog"
import {
  useAdminRouteAccessPolicyQuery,
  useAdminRouteBaseAccessPolicyQuery,
  useAdminRoutesQuery,
  useClearRouteAccessPolicyMutation,
  useUpdateRouteAccessPolicyMutation,
  useUpdateRouteStatusMutation,
} from "../hooks/admin-queries"

/** Manage the existing capability gate here instead of in module administration.
 * Keep its stored ACL and inheritance intact; the independent tool policy still applies. */
export function TranslatorAccessManagement() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data: routes, isLoading, isError } = useAdminRoutesQuery()
  // The backend enforces this canonical id, not an arbitrary component alias.
  const route = routes?.find((entry) => entry.id === "translate")
  const statusMutation = useUpdateRouteStatusMutation()
  const accessMutation = useUpdateRouteAccessPolicyMutation()
  const clearMutation = useClearRouteAccessPolicyMutation()
  const accessQuery = useAdminRouteAccessPolicyQuery(route?.id, open)
  const baseQuery = useAdminRouteBaseAccessPolicyQuery(open)
  const policyUnavailable = accessQuery.isError || baseQuery.isError

  const reportError = () => {
    toast.error(t("translatorTool.admin.saveError"))
  }

  return (
    <>
      <section
        aria-label={t("translatorTool.admin.title")}
        className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-md border p-4"
      >
        <div className="max-w-prose space-y-1">
          <h3 className="text-base">{t("translatorTool.admin.title")}</h3>
          <p className="text-muted-foreground text-sm">{t("translatorTool.admin.description")}</p>
          {!isLoading && (!route || isError) && (
            <p className="text-sm" role="alert">
              {t("translatorTool.admin.unavailable")}
            </p>
          )}
          {open && policyUnavailable && (
            <div className="space-y-2 text-sm" role="alert">
              <p>{t("translatorTool.admin.unavailable")}</p>
              <Button
                className="min-h-10"
                size="sm"
                variant="outline"
                onClick={() => {
                  void accessQuery.refetch()
                  void baseQuery.refetch()
                }}
              >
                {t("translatorTool.admin.retry")}
              </Button>
            </div>
          )}
        </div>
        {isLoading ? (
          <Skeleton className="h-8 w-40" />
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            <Label
              className="flex min-h-10 cursor-pointer items-center gap-2"
              htmlFor="translator-access-enabled"
            >
              <span>{t("translatorTool.admin.enabled")}</span>
              <Switch
                checked={route?.enabled === true}
                disabled={!route || isError || statusMutation.isPending}
                id="translator-access-enabled"
                onCheckedChange={async (enabled) => {
                  if (!route) return
                  try {
                    await statusMutation.mutateAsync({ routeId: route.id, enabled })
                  } catch {
                    reportError()
                  }
                }}
              />
            </Label>
            <Button
              className="min-h-10"
              disabled={!route || isError || statusMutation.isPending}
              size="sm"
              variant="outline"
              onClick={() => setOpen(true)}
            >
              {route?.access_configured ? (
                <ShieldCheck className="mr-2 size-4" />
              ) : (
                <Shield className="mr-2 size-4" />
              )}
              {t("translatorTool.admin.access")}
            </Button>
          </div>
        )}
      </section>

      <AccessPolicyDialog
        description={t("translatorTool.admin.accessDescription")}
        isLoading={accessQuery.isLoading || baseQuery.isLoading}
        isSaving={accessMutation.isPending || clearMutation.isPending}
        open={open && !!route && !isError && !policyUnavailable}
        policy={accessQuery.data}
        title={t("translatorTool.admin.access")}
        inherit={{
          enabled: true,
          inheritByDefault: route?.access_overridden !== true,
          policy: baseQuery.data,
          label: t("translatorTool.admin.inherit"),
          description: t("translatorTool.admin.inheritDescription"),
          onSaveInherited: async () => {
            if (!route || policyUnavailable) throw new Error("Translator access is unavailable")
            try {
              await clearMutation.mutateAsync({ routeId: route.id })
            } catch (error) {
              reportError()
              throw error
            }
          },
        }}
        onOpenChange={setOpen}
        onSave={async (policy) => {
          if (!route || policyUnavailable) throw new Error("Translator access is unavailable")
          try {
            await accessMutation.mutateAsync({ routeId: route.id, policy })
          } catch (error) {
            reportError()
            throw error
          }
        }}
      />
    </>
  )
}
